import { Injectable } from '@angular/core';
import { App } from '@df/core/app';

/**
 * Web storage.
 *
 * @export
 * @interface IWebStorage
 */
export interface IWebStorage {
    get(key: string): any;
    set(key: string, value: any): void;
    remove(key: string): void;
}

@Injectable({
    providedIn: 'root'
})
export class GlobalStorageService implements IWebStorage {
    /**
     * Storage instance.
     *
     * @protected
     * @type {Storage}
     * @memberof LocalStorage
     */
    protected storage: Storage | undefined = typeof window === 'undefined' ? undefined : window.localStorage;

    constructor(protected app: App) {
        this.register();
    }

    protected register() {
        this.app.globalStorageService = this;
    }

    /**
     * Key getter.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string} key
     * @return {string}
     */
    public getKey(key: string): string {
        return 'ngStorage-glob-' + key;
    }

    /**
     * Gets data from strage.
     *
     * @param {string} key
     * @returns {*}
     * @memberof LocalStorage
     */
    get(key: string): any {
        return this.fromJson(this.storage?.getItem(this.getKey(key)) || undefined);
    }

    /**
     * Sets data in storage.
     *
     * @param {string} key
     * @param {*} value
     * @memberof LocalStorage
     */
    set(key: string, value: any): void {
        if (value !== this.get(key)) {
            if (value === undefined || value === null) {
                this.remove(key);
            } else {
                this.storage?.setItem(this.getKey(key), this.toJson(value));
            }
        }
    }

    /**
     * Removes data from storage.
     *
     * @param {string} key
     * @memberof LocalStorage
     */
    remove(key: string): void {
        this.storage?.removeItem(this.getKey(key));
    }

    /**
     * Helper converting to jsong.
     *
     * @protected
     * @param {*} value
     * @returns {string}
     * @memberof LocalStorage
     */
    protected toJson(value: any): string {
        return typeof value === 'string' ? value : JSON.stringify(value);
    }

    /**
     * Helper converting from json.
     *
     * @protected
     * @param {string} value
     * @returns {*}
     * @memberof LocalStorage
     */
    protected fromJson(value: string | undefined): any {
        if (!value) {
            return value;
        }
        try {
            return JSON.parse(value);
        } catch (e) {
            return value;
        }
    }
}

/**
 * Register service in app
 */
declare module '@df/core/app' {
    interface App {
        globalStorageService: GlobalStorageService;
    }
}
